import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/Admin/AdminLayout';
import DataTable from '../../components/Admin/DataTable';
import blogService from '../../services/blogService';

export default function BlogList() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        limit: 100,
        showAll: true,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (categoryFilter) {
        params.category = categoryFilter;
      }

      const response = await blogService.getAllBlogs(params);

      if (response.success && response.data) {
        const blogs = Array.isArray(response.data) ? response.data : [];
        setBlogs(blogs);
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Failed to load blogs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, categoryFilter]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await blogService.getCategories();
      if (response.success && response.data) {
        setCategories(response.data.categories || []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, [fetchBlogs, fetchCategories]);

  const navigate = useNavigate();

  const handleEdit = (item) => {
    navigate(`/admin/blogs/edit/${item.id}`);
  };

  const handleDelete = async (item) => {
    const { id, title } = item;
    if (!window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await blogService.deleteBlog(id);

      if (response.success) {
        setBlogs(blogs.filter(blog => blog.id !== id));
      } else {
        alert('Failed to delete blog. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting blog:', err);
      alert('Error deleting blog. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns = [
    {
      header: 'Blog',
      accessor: 'title',
      render: (item) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-12 w-16 mr-4 bg-gray-800 rounded overflow-hidden">
            <img
              src={item.image || '/assets/images/blog-1.png'}
              alt={item.title}
              className="h-full w-full object-cover"
              onError={(e) => { e.target.src = '/assets/images/blog-1.png'; }}
            />
          </div>
          <div>
            <div className="font-medium text-white">{item.title}</div>
            <div className="text-xs text-gray-500 line-clamp-1">{item.description}</div>
          </div>
        </div>
      )
    },
    {
      header: 'Category',
      accessor: 'category',
      render: (item) => (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-900/30 text-blue-400 border border-blue-800/50">
          {item.category}
        </span>
      )
    },
    {
      header: 'Status',
      accessor: 'published',
      render: (item) => {
        const isPublished = item.published === 1 || item.is_published === true;
        return (
          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${isPublished
            ? 'bg-green-900/30 text-green-400 border border-green-800/50'
            : 'bg-yellow-900/30 text-yellow-400 border border-yellow-800/50'
            }`}>
            {isPublished ? 'Published' : 'Draft'}
          </span>
        );
      }
    },
    {
      header: 'Date',
      accessor: 'created_at',
      render: (item) => <span className="text-gray-400">{formatDate(item.created_at)}</span>
    }
  ];

  return (
    <AdminLayout>
      <div className="flex flex-col space-y-4">
        {/* Header with Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 flex-none">
          <div className="flex space-x-4 flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              </div>
              <input
                type="text"
                placeholder="Search blogs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#1a1025] border border-white/10 rounded-lg focus:outline-none focus:border-blue-500 text-gray-300 placeholder-gray-500"
              />
            </div>

            {/* Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 bg-[#1a1025] border border-white/10 rounded-lg focus:outline-none focus:border-blue-500 text-gray-300"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <Link
            to="/admin/blogs/new"
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors shadow-lg hover:shadow-blue-600/20"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create New Blog
          </Link>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-md flex-none">
            {error}
          </div>
        )}

        {/* Blogs List */}
        <div>
          <DataTable
            columns={columns}
            data={blogs}
            isLoading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </div>
      </div>
    </AdminLayout>
  );
}

